import { useState } from "react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CheckCircle2, Mail, Clock, ArrowRight } from "lucide-react";
import { CONSULTATION_URL } from "@/data/constants";
import { trackConversion } from "@/hooks/useSiteAnalytics";
import useSEO, { breadcrumbSchema } from "@/hooks/useSEO";

const BEVERAGE_TYPES = [
  "Purified Water", "Alkaline Water", "Spring Water", "Sparkling Water",
  "CBD Water", "CBD Water for Pets", "Protein Drinks", "Energy Drinks",
  "Sports Drinks", "Functional Beverages", "Coffee", "Tea",
  "Supplements", "Juice", "Other",
];

const SERVICES = [
  "Product Development", "Branding & Artwork", "Packaging",
  "Manufacturing", "Logistics & Shipping", "Reorder / Repeat Production",
  "Full End-to-End Launch", "Other",
];

const Contact = () => {
  useSEO({
    title: "Contact — FLOWTALIS™",
    description: "Get in touch with FLOWTALIS™ — premium private-label beverage manufacturing and brand development. Formulation, packaging, production, artwork, and shipping in one partner.",
    canonical: "https://flowtalis.com/contact",
    jsonLd: breadcrumbSchema([
      { label: "Home", href: "/" },
      { label: "Contact", href: "/contact" },
    ]),
  });
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", email: "", phone: "", zip: "",
    beverage_type: "", service_interested_in: "",
    preferred_packaging: "", estimated_quantity: "",
    additional_info: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackConversion("contact_submit", {
        beverage_type: form.beverage_type || undefined,
        service: form.service_interested_in || undefined,
        has_company: !!form.company,
      });
      setSent(true);
      toast.success("Message sent. We will be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-white py-24 border-b border-[var(--ft-border)]">
        <div className="ft-container max-w-3xl ft-fade-in">
          <div className="ft-eyebrow mb-5">Contact</div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-[clamp(2.25rem,4.5vw,3.75rem)] font-bold leading-[1.02] tracking-tight text-[var(--ft-black)]">
            Let&apos;s build your <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>beverage brand</span>.
          </h1>
          <p className="mt-8 text-[var(--ft-charcoal)] text-lg leading-relaxed max-w-2xl">
            Whether you&apos;re launching your first beverage or expanding an existing product line, our team is here to help guide you from concept to production.
          </p>
        </div>
      </section>

      {/* Contact info + form */}
      <section className="py-20 bg-white">
        <div className="ft-container grid lg:grid-cols-[380px_1fr] gap-10">
          {/* Info */}
          <aside className="space-y-4">
            <div className="ft-card">
              <div className="flex items-center gap-3 mb-3">
                <Mail size={20} strokeWidth={1.5} style={{ color: "var(--ft-gold)" }} />
                <div className="ft-eyebrow">Sales &amp; General Inquiries</div>
              </div>
              <div className="ft-gold-rule my-3" />
              <a href="mailto:hello@flowtalis.com" className="text-lg font-semibold text-[var(--ft-black)] hover:text-[var(--ft-gold)] transition-colors" data-testid="contact-email">
                hello@flowtalis.com
              </a>
            </div>
            <div className="ft-card">
              <div className="flex items-center gap-3 mb-3">
                <Clock size={20} strokeWidth={1.5} style={{ color: "var(--ft-gold)" }} />
                <div className="ft-eyebrow">Business Hours</div>
              </div>
              <div className="ft-gold-rule my-3" />
              <div className="text-[var(--ft-charcoal)]">
                <div className="font-semibold">Monday — Friday</div>
                <div className="text-sm text-[var(--ft-muted)] mt-1">9:00 AM — 5:00 PM Eastern Time</div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div>
            {sent ? (
              <div className="ft-card text-center py-16" data-testid="contact-success">
                <CheckCircle2 size={48} style={{ color: "var(--ft-gold)" }} className="mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-[var(--ft-black)]">Message sent.</h2>
                <p className="text-[var(--ft-muted)] mt-2">We will respond within one business day.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="ft-card space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="ft-label">First Name*</label>
                    <input required data-testid="contact-first-name" className="ft-input" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} />
                  </div>
                  <div>
                    <label className="ft-label">Last Name*</label>
                    <input required data-testid="contact-last-name" className="ft-input" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="ft-label">Company Name</label>
                    <input data-testid="contact-company" className="ft-input" value={form.company} onChange={(e) => update("company", e.target.value)} />
                  </div>
                  <div>
                    <label className="ft-label">Zip Code</label>
                    <input data-testid="contact-zip" className="ft-input" value={form.zip} onChange={(e) => update("zip", e.target.value)} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="ft-label">Email Address*</label>
                    <input required type="email" data-testid="contact-email-input" className="ft-input" value={form.email} onChange={(e) => update("email", e.target.value)} />
                  </div>
                  <div>
                    <label className="ft-label">Phone Number</label>
                    <input data-testid="contact-phone" className="ft-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="ft-label">Beverage Type</label>
                    <select data-testid="contact-beverage-type" className="ft-input" value={form.beverage_type} onChange={(e) => update("beverage_type", e.target.value)}>
                      <option value="">Select category…</option>
                      {BEVERAGE_TYPES.map((b) => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="ft-label">Service Interested In</label>
                    <select data-testid="contact-service" className="ft-input" value={form.service_interested_in} onChange={(e) => update("service_interested_in", e.target.value)}>
                      <option value="">Select service…</option>
                      {SERVICES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="ft-label">Preferred Packaging</label>
                  <div className="grid sm:grid-cols-3 gap-3 mt-2" data-testid="contact-packaging">
                    {[
                      { v: "plastic-bottle", l: "Plastic Bottle" },
                      { v: "aluminum-bottle", l: "Aluminum Bottle" },
                      { v: "aluminum-can", l: "Aluminum Can (Spring Water)" },
                    ].map((opt) => (
                      <label key={opt.v}
                             className={`fx-chip justify-center cursor-pointer ${form.preferred_packaging === opt.v ? "is-active" : ""}`}
                             data-testid={`contact-packaging-${opt.v}`}>
                        <input type="radio" name="packaging" value={opt.v} className="sr-only"
                               checked={form.preferred_packaging === opt.v}
                               onChange={(e) => update("preferred_packaging", e.target.value)} />
                        {opt.l}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="ft-label">Estimated Quantity — How many pallets?</label>
                  <select data-testid="contact-quantity" className="ft-input" value={form.estimated_quantity} onChange={(e) => update("estimated_quantity", e.target.value)}>
                    <option value="">Select quantity…</option>
                    <option value="1-3">1–3 pallets</option>
                    <option value="3-6">3–6 pallets</option>
                    <option value="6-10">6–10 pallets</option>
                    <option value="10+">10+ pallets</option>
                  </select>
                </div>
                <div>
                  <label className="ft-label">Message*</label>
                  <textarea required rows={4} data-testid="contact-message" className="ft-input" value={form.message} onChange={(e) => update("message", e.target.value)} />
                </div>
                <div>
                  <label className="ft-label">Additional Information</label>
                  <textarea rows={4} data-testid="contact-additional-info" className="ft-input"
                            placeholder="Please share anything else you'd like us to know about your project."
                            value={form.additional_info} onChange={(e) => update("additional_info", e.target.value)} />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit" className="ft-btn-gold">
                  {loading ? "Sending…" : "Send Message"} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="py-24 bg-[var(--ft-light-gray)] text-center">
        <div className="ft-container max-w-2xl">
          <div className="ft-eyebrow mb-5">Book a Consultation</div>
          <span className="ft-gold-rule-lg mx-auto" />
          <h2 className="mt-6 text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
            Prefer a <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>guided conversation</span>?
          </h2>
          <p className="text-[var(--ft-muted)] mt-6 mb-8 text-lg">
            Schedule a consultation and receive a launch scope tailored to your category, MOQ, and timeline.
          </p>
          <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-gold" data-testid="contact-book-consultation">
            Book a Consultation <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Contact;
